import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { DecorativeIcon } from './DecorativeIcon';
import { addProject, ApiError, type TeamSummary } from '../api';
import type { TeamJwt } from '../auth/teamJwt';
import { PanelState } from '../components/Chrome';
import { getProjectRouteId } from '../projectSelection';
import { orgDocsPath } from '../routing';
import { ALERT_ERROR, BUTTON_GHOST, BUTTON_PRIMARY, BUTTON_SECONDARY, CARD, FIELD, FIELD_LABEL, SECTION_SUBTITLE, SECTION_TITLE } from '../controlStyles';
import { projectDisplayName, projectSubtitle } from './memberDirectory';

/** The server re-checks the role; this only decides whether to offer it. */
function isTeamAdmin(team: TeamSummary): boolean {
  return team.role === 'admin' || team.role === 'owner';
}

function AddProjectForm({
  team,
  getTeamJwt,
  reloadTeams,
}: {
  team: TeamSummary;
  getTeamJwt: () => Promise<TeamJwt>;
  reloadTeams: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await addProject(await getTeamJwt(), team.orgId, trimmed);
      await reloadTeams();
      setName('');
      setOpen(false);
    } catch (cause) {
      console.error('[organization] add project failed', cause);
      setError(cause instanceof ApiError && cause.status === 403
        ? 'Only a team admin can add projects.'
        : cause instanceof ApiError && cause.status >= 400 && cause.status < 500
          ? `The project was not added: ${cause.message.replace(/\.?$/, '.')}`
          : 'The project was not added. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button className={`organization-add-project ${BUTTON_SECONDARY} justify-self-start`} type="button" onClick={() => setOpen(true)}>
        <DecorativeIcon icon="add" size={16} />Add a project
      </button>
    );
  }
  return (
    <form className="organization-add-project-form grid gap-2" onSubmit={(event) => void submit(event)}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          className={`${FIELD} max-w-[20rem]`}
          aria-label="Project name"
          placeholder="Project name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={120}
          disabled={busy}
          autoFocus
        />
        <button className={BUTTON_PRIMARY} type="submit" disabled={busy || !name.trim()}>{busy ? 'Adding…' : 'Add project'}</button>
        <button className={BUTTON_GHOST} type="button" disabled={busy} onClick={() => { setOpen(false); setError(null); }}>Cancel</button>
      </div>
      {error && <div className={ALERT_ERROR} role="alert">{error}</div>}
    </form>
  );
}

export function OrganizationOverview({
  team,
  getTeamJwt,
  reloadTeams,
}: {
  team: TeamSummary;
  getTeamJwt: () => Promise<TeamJwt>;
  reloadTeams: () => Promise<void>;
}) {
  return (
    <div
      className="organization-overview flex w-full flex-col gap-6"
      style={{ containerType: 'inline-size', containerName: 'organization-overview' }}
    >
      <section className="organization-summary flex flex-col gap-3">
        <header>
          <h2 className={SECTION_TITLE}>Organization overview</h2>
          <p className={SECTION_SUBTITLE}>Projects, membership, and access for this organization.</p>
        </header>
        <div className="grid grid-cols-1 gap-3 [@container_organization-overview_(min-width:40rem)]:grid-cols-2 [@container_organization-overview_(min-width:76rem)]:grid-cols-4">
          <article className={CARD}>
            <span className={FIELD_LABEL}>Your role</span>
            <h3 className="mt-2 mb-1 text-[1.15rem] font-semibold capitalize text-nim">{team.role}</h3>
            <p className={SECTION_SUBTITLE}>All privileged writes are checked again by the organization TeamRoom.</p>
          </article>
          <article className={CARD}>
            <span className={FIELD_LABEL}>Membership</span>
            <h3 className="mt-2 mb-1 text-[1.15rem] font-semibold capitalize text-nim">
              {team.membershipType.replaceAll('_', ' ')}
            </h3>
            <p className={SECTION_SUBTITLE}>
              {team.projects.length} {team.projects.length === 1 ? 'project' : 'projects'} in this organization
            </p>
          </article>
        </div>
      </section>

      <section className="organization-projects flex flex-col gap-3">
        <header>
          <h2 className={SECTION_TITLE}>Projects</h2>
          <p className={SECTION_SUBTITLE}>
            Each project has its own document tree. Switch between them from the top of the Shared Docs pane.
          </p>
        </header>
        {isTeamAdmin(team) && <AddProjectForm team={team} getTeamJwt={getTeamJwt} reloadTeams={reloadTeams} />}
        {team.projects.length === 0 ? (
          <PanelState tone="empty">
            <strong>No projects available</strong>
            <span>This organization has no projects yet.</span>
          </PanelState>
        ) : (
          <div className="organization-project-list grid grid-cols-1 gap-3 [@container_organization-overview_(min-width:40rem)]:grid-cols-2 [@container_organization-overview_(min-width:76rem)]:grid-cols-3">
            {team.projects.map((project) => {
              const subtitle = projectSubtitle(project);
              return (
                <Link
                  className="organization-project-card grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2.5 rounded-[0.7rem] border border-nim bg-nim px-4 py-3 no-underline shadow-[var(--console-shadow)] transition-colors duration-150 hover:border-nim-focus hover:bg-nim-hover"
                  key={project.projectId}
                  to={orgDocsPath(team, getProjectRouteId(project))}
                >
                  <DecorativeIcon icon="folder" size={20} className="text-nim-muted" />
                  <span className="min-w-0">
                    <strong className="block truncate text-[0.85rem] font-semibold text-nim">
                      {projectDisplayName(project)}
                    </strong>
                    {subtitle && <small className="mt-0.5 block truncate text-[0.7rem] text-nim-muted">{subtitle}</small>}
                  </span>
                  <DecorativeIcon icon="chevron_right" size={18} className="text-nim-muted" />
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
